/* import __COLOCATED_TEMPLATE__ from './conversation-explore-panel.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type InboxApiService from 'embercom/services/inbox-api';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import type Conversation from 'embercom/objects/inbox/conversation';

interface Args {
  showSidePanel: boolean;
  onClose: () => void;
  actionName: string;
  actionSummary: string;
  conversationIds: string[];
  conversationCount: number;
}

interface Signature {
  Args: Args;
}

const CONVERSATION_PAGE_SIZE = 100;

export default class ConversationExplorePanel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare inboxApi: InboxApiService;

  @tracked totalHits = 0;
  @tracked pageFrom = 0;
  @tracked loadedConversations: ConversationExploreConversation[] = [];
  @tracked selectedConversationId = 0;

  fetchDataTask = taskFor(this.loadConversations);

  conversationData = trackedTask(this, this.fetchDataTask, () => [
    this.args.showSidePanel,
    this.args.conversationIds,
    this.pageFrom,
  ]);

  @action
  onClose() {
    this.args.onClose();
  }

  @action
  onConversationSelected(conversationId: number) {
    this.selectedConversationId = conversationId;
  }

  @action
  loadMoreConversations() {
    this.pageFrom += CONVERSATION_PAGE_SIZE;
  }

  @action
  trackAnalyticsEvent(_action: string, _object: string, _extraContext: Record<string, any>) {
    // No-op for now - could be implemented later if analytics tracking is needed.
  }

  get conversationIdsAsNumbers(): number[] {
    return this.args.conversationIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  }

  get conversationFetchError() {
    return this.fetchDataTask.lastErrored?.error as any;
  }

  @restartableTask
  *loadConversations(): TaskGenerator<void> {
    // Don't load if the panel is not shown or we don't have valid conversation IDs
    if (
      !this.args.showSidePanel ||
      !this.args.conversationIds ||
      this.args.conversationIds.length === 0 ||
      this.conversationIdsAsNumbers.length === 0
    ) {
      this.loadedConversations = [];
      this.totalHits = 0;
      this.selectedConversationId = 0;
      return;
    }

    if (this.pageFrom === 0) {
      this.loadedConversations = [];
    }

    let conversationIdsToFetch = this.conversationIdsAsNumbers.slice(
      this.pageFrom,
      this.pageFrom + CONVERSATION_PAGE_SIZE,
    );

    let conversationPromises = conversationIdsToFetch.map((conversationId) =>
      this.inboxApi.fetchConversation(conversationId),
    );

    let conversations: Conversation[] = yield Promise.all(conversationPromises);

    let newConversations = conversations.map((conversation) => ({
      id: Number(conversation.id),
      userName: conversation.firstParticipant?.name || '',
      userPseudonym: conversation.firstParticipant?.pseudonym || '',
      topicSummary: conversation.attributesById?.['ai_insights_conversation_summary']?.value || '',
      subtopic: conversation.attributesById?.['ai_insights_subtopic']?.value || '',
      topic: conversation.attributesById?.['ai_insights_topic']?.value || '',
      cxExplanation: this.appService.app.useNewCxSummary
        ? conversation.attributesById?.['customer_experience_summary']?.value || ''
        : conversation.attributesById?.['customer_experience_explanation']?.value || '',
      cxScore: conversation.attributesById?.['customer_experience_rating']?.value || 0,
      cxAttributes: conversation.attributesById?.['customer_experience_attributes']?.value || '',
    })) as ConversationExploreConversation[];

    this.loadedConversations = [...this.loadedConversations, ...newConversations];
    this.totalHits = this.conversationIdsAsNumbers.length;

    // Set the first conversation as selected, following the same pattern as suggestion-explore-panel
    this.selectedConversationId =
      this.loadedConversations.length > 0 ? Number(this.loadedConversations[0].id) : 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::ConversationExplorePanel': typeof ConversationExplorePanel;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/conversation-explore-panel': typeof ConversationExplorePanel;
  }
}
